import React, { useState, useEffect, useMemo } from 'react';
import { string, object, oneOfType, array } from 'prop-types';
import { Tab, TabList, Tabs, TabPanel } from 'react-context-tabs';
import classNames from 'classnames/bind';

import TabAbout from '../TabAbout';
import TabOeNumbers from '../TabOeNumbers';
import TabLinkedVehicles from '../TabLinkedVehicles';
import TabPartList from '../TabPartList';
import TabReferenceReplaceList from '../TabReferenceReplaceList';

import { withLabels } from 'containers/withLabels';
import { changeRoute, activeProductTab } from 'utils/tabsHelper';
import { VALEO_BRANDS, getIsValeoBrand } from 'utils/constantsHelper';
import { currentLanguage } from 'utils/currentLanguage';
import TabResources from 'pages/Product/components/TabResources';

import styles from './ProductTabs.module.scss';
const cx = classNames.bind(styles);

const ProductTabs = ({ className, data, match, labels, dataBulletins }) => {
  const [selectedTabId, setSelectedTabId] = useState(
    activeProductTab(match.params.tabName)
  );

  const createTabChangeAction = (tab) => {
    const productTabs = document.getElementById('product-tabs');
    changeRoute(tab);
    setSelectedTabId(tab);
    // Added min-height style to prevent jumping tabs on click.
    productTabs.style.minHeight = productTabs.offsetHeight + 'px';
  };

  const { hasPartList } = data.product.assignedArticle;
  const documents = data.product.articleDocuments.array
    ? data.product.articleDocuments.array.filter((item) => item.docTypeId === 4)
    : [];
  const { brandNo } = data.product.assignedArticle;

  const isValeoProduct = useMemo(() => getIsValeoBrand(VALEO_BRANDS, Number(brandNo)), [VALEO_BRANDS, brandNo]);
  const isDisplayTechDocs =
    !!(isValeoProduct && (dataBulletins.length || documents.length));

  const productsReplaces = useMemo(() => {
    const references = data.product.replacedNumber.array;
    return references ? references.filter(ref => ref.replaceArticleId) : null;
  }, [data]);

  const productsReplacedBy = useMemo(() => {
    const references = data.product.replacedByNumber.array;
    return references ? references.filter(ref => ref.replaceArticleId) : null;
  }, [data]);

  useEffect(() => {
    setSelectedTabId(activeProductTab(match.params.tabName));
  }, [match.params.tabName]);

  // Open technical resources tab by default for br domain
  useEffect(() => {
    if (!isDisplayTechDocs || selectedTabId !== 'about' || currentLanguage !== 'pt-br') return;
    createTabChangeAction('technical-resources');
  }, [isDisplayTechDocs, currentLanguage]);

  return (
    <Tabs
      selectedTabId={selectedTabId}
      onTabChange={createTabChangeAction}
      id="product-tabs"
      className={cx('tabs', className)}
    >
      <TabList className={cx('tabs-links')}>
        <Tab tabId="about" className={cx('item')}>
          {labels.productAboutTab}
        </Tab>
        {isDisplayTechDocs && (
          <Tab tabId="technical-resources" className={cx('item')}>
            {labels.productResourcesTab + ' '}(
            {dataBulletins.length + documents.length})
          </Tab>
        )}
        {data.product.oenNumbers && (
          <Tab tabId="oe-numbers" className={cx('item')}>
            {labels.productOeNumbersTab}
          </Tab>
        )}
        <Tab tabId="linked-vehicles" className={cx('item')}>
          {labels.productLinkedVehiclesTab}
        </Tab>
        {hasPartList && (
          <Tab tabId="parts-list" className={cx('item')}>
            {labels.productPartsListTab}
          </Tab>
        )}
        {productsReplaces && productsReplaces.length > 0 && (
          <Tab tabId="replaces" className={cx('item')}>
            {labels.replacesLabel}
          </Tab>
        )}
        {productsReplacedBy && productsReplacedBy.length > 0 && (
          <Tab tabId="replaced-by" className={cx('item')}>
            {labels.replacedLabel}
          </Tab>
        )}
      </TabList>
      <TabPanel tabId="about">
        {data.product && data.product.articleAttributes ? (
          <TabAbout data={data.product.articleAttributes.array} />
        ) : null}
      </TabPanel>
      {isDisplayTechDocs && (
        <TabPanel tabId="technical-resources">
          <TabResources data={dataBulletins} documents={documents} />
        </TabPanel>
      )}
      {data.product.oenNumbers && (
        <TabPanel tabId="oe-numbers">
          <TabOeNumbers dataOeNumbers={data.product.oenNumbers.array} />
        </TabPanel>
      )}
      <TabPanel tabId="linked-vehicles">
        <TabLinkedVehicles
          dataOeNumbers={data.product.oenNumbers.array}
          productId={data.product.assignedArticle.articleId}
        />
      </TabPanel>
      {hasPartList && (
        <TabPanel tabId="parts-list">
          <TabPartList articleId={data.product.assignedArticle.articleId} />
        </TabPanel>
      )}
      {productsReplaces && productsReplaces.length > 0 && (
        <TabPanel tabId="replaces">
          <TabReferenceReplaceList references={productsReplaces} />
        </TabPanel>
      )}
      {productsReplacedBy && productsReplacedBy.length > 0 && (
        <TabPanel tabId="replaced-by">
          <TabReferenceReplaceList references={productsReplacedBy} />
        </TabPanel>
      )}
    </Tabs>
  );
};

ProductTabs.propTypes = {
  className: string,
  match: object.isRequired,
  data: object.isRequired,
  dataBulletins: oneOfType([object, array]).isRequired,
  labels: object.isRequired,
};

ProductTabs.defaultProps = {
  className: null,
};

export default withLabels(ProductTabs);



// WEBPACK FOOTER //
// ./src/pages/Product/components/ProductTabs/ProductTabs.js